/*!
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import classNames from 'classnames';
import type React from 'react';
import { type ReactNode, useMemo } from 'react';

import {
  FormFieldLabel,
  FormFieldMessage,
  useFormField,
} from '../_common/form-control';
import { customLabelAndNoAriaLabelWarningMessage } from '../_common/messages';
import {
  type CommonProps,
  type HtmlAttributes,
  type TooltipObjectProps,
} from '../_common/types';
import { needleWarningMessage } from '../_common/utils';

/**
 *
 *
 * Types
 *
 *
 */

/**
 * @remarks
 * When `moreInformationText` is provided, a tooltip is rendered next to the label, and the `tooltipProps` prop is used to configure the tooltip. The `TooltipObjectProps` type is defined as: `{ root?: Partial<React.ComponentProps<typeof Tooltip>>; trigger?: Partial<React.ComponentProps<typeof Tooltip.Trigger>>; content?: Partial<React.ComponentProps<typeof Tooltip.Content>>; }`, see the Tooltip component for more details.
 */
type TextAreaProps = {
  // TODO v5: inconsistent naming. The label is optional here and pairs with the
  // `ariaLabel` prop below, while Combobox requires the label and clips it with
  // `hasVisuallyHiddenLabel`, and TextInput has no aria label prop at all.
  /** The label displayed above the textarea */
  label?: ReactNode;

  /** Whether the textarea should take the full available width */
  isFluid?: boolean;

  /** Assistive text displayed below the textarea */
  helpText?: string | ReactNode;

  /** Error message displayed below the textarea. When provided, it overrides `helpText` and shows an error icon */
  errorText?: string | ReactNode;

  /** Size of the textarea */
  size?: 'small' | 'medium' | 'large';

  // TODO v5: inconsistent naming, and one prop doing two jobs. The same switch
  // is `showRequiredOrOptionalLabel` on TextInput and `hasRequiredIndicator` on
  // Combobox and on the internal form control this maps onto. Here it also
  // decides both whether the indicator shows at all and which word it shows,
  // because TextArea exposes no `isRequired` to pair it with: requiredness has
  // to be set through htmlAttributes.required, and exposing `isRequired` on its
  // own would silently render no indicator.
  /** Whether to display the Optional label next to the field label */
  isOptional?: boolean;

  /** Text displayed in the information tooltip shown next to the label */
  moreInformationText?: React.ReactNode;

  /** Props for the Tooltip component used by the information icon */
  tooltipProps?: TooltipObjectProps;

  /** Whether the textarea is disabled */
  isDisabled?: boolean;

  /** Additional HTML attributes passed to the textarea element */
  htmlAttributes?: HtmlAttributes<'textarea'>;

  // TODO v5: inconsistent naming. This replaces the accessible name rather than
  // hiding the label, which is a second way of naming the control alongside
  // `label` above. TextInput reaches for htmlAttributes['aria-label'] for the
  // same case and Combobox clips the label with `hasVisuallyHiddenLabel`.
  /** Aria label for accessibility when no visible label is provided */
  ariaLabel?: string;

  /** Additional class names */
  className?: string;

  /** Additional styles applied to the root element */
  style?: React.CSSProperties;

  /** Whether the textarea is read-only */
  isReadOnly?: boolean;

  /** The current value of the textarea */
  value?: React.ComponentPropsWithoutRef<'textarea'>['value'];

  /** Placeholder text displayed when the textarea is empty */
  placeholder?: React.ComponentPropsWithoutRef<'textarea'>['placeholder'];
};

/**
 *
 *
 * TextArea Component
 *
 *
 */

export const TextArea = ({
  label,
  isFluid = false,
  errorText,
  helpText,
  isOptional = false,
  moreInformationText,
  size = 'medium',
  ariaLabel,
  tooltipProps,
  isDisabled = false,
  className,
  isReadOnly = false,
  style,
  value,
  placeholder,
  htmlAttributes,
  ref,
  ...restProps
}: CommonProps<'textarea', TextAreaProps>) => {
  const { field, controlProps } = useFormField({
    errorText,
    hasRequiredIndicator: isOptional,
    helpText,
    label,
  });
  const containerWrappingClasses = classNames(
    `ndl-text-area ndl-type-text`,
    className,
    {
      'ndl-disabled': isDisabled,
      'ndl-has-error': errorText,
      'ndl-has-icon': errorText,
      'ndl-has-trailing-icon': errorText,
      'ndl-large': size === 'large',
      'ndl-medium': size === 'medium',
      'ndl-small': size === 'small',
    },
  );

  const hasEmptyLabelValue = !label || label === '';

  const labelWrappingClasses = classNames('ndl-text-area-label', {
    'ndl-fluid': isFluid,
  });

  const isCustomLabel = label && typeof label !== 'string';
  const hasCustomLabelAndNoAriaLabel = isCustomLabel && !ariaLabel;

  useMemo(() => {
    if (!label && !ariaLabel) {
      needleWarningMessage(
        'A TextArea without a label does not have an aria label, be sure to include an aria label for screen readers link: https://dequeuniversity.com/rules/axe/4.2/label?application=axeAPI',
      );
    }

    if (hasCustomLabelAndNoAriaLabel) {
      needleWarningMessage(customLabelAndNoAriaLabelWarningMessage);
    }
  }, [label, ariaLabel, hasCustomLabelAndNoAriaLabel]);

  return (
    <div className={containerWrappingClasses}>
      {/* We enable implicit label wrapping */}
      {/* Source: https://dequeuniversity.com/rules/axe/4.2/label?application=axeAPI */}
      <label className={labelWrappingClasses}>
        {!hasEmptyLabelValue && (
          <FormFieldLabel
            field={field}
            size={size}
            association="implicit"
            moreInformationText={moreInformationText}
            tooltipProps={tooltipProps}
          />
        )}
        <div className="ndl-text-area-wrapper">
          <textarea
            disabled={isDisabled}
            ref={ref}
            aria-label={ariaLabel}
            readOnly={isReadOnly}
            value={value}
            placeholder={placeholder}
            style={style}
            {...controlProps}
            {...restProps}
            {...htmlAttributes}
          />
        </div>
      </label>
      <FormFieldMessage field={field} size={size} />
    </div>
  );
};
